import { ajax, encodePathVariable } from 'cadenza/api-client/api';
import {
  MIMETYPE_DISY_POST_MESSAGE,
  PARAMETER_CONDITION_DEFINITION_ID,
  PARAMETER_CONDITION_HASH,
  PARAMETER_CONDITION_SET_ID
} from 'cadenza/api-client/api-constants';
import { MIMETYPE_JSON } from 'cadenza/model/media-type';
import type { ConditionId } from 'cadenza/workbook/model/condition-id';

export interface ConditionApiOptions {
  conditionSetId: string;
  conditionName: string;
  conditionId: ConditionId;
  conditionValuesSetHashGetter: () => string;
}

export class ConditionApi {

  readonly #conditionSetId;
  readonly #conditionName;
  readonly #conditionId;
  readonly #conditionValuesSetHashGetter;

  constructor ({ conditionSetId, conditionName, conditionId, conditionValuesSetHashGetter }: ConditionApiOptions) {
    this.#conditionSetId = conditionSetId;
    this.#conditionName = conditionName;
    this.#conditionId = conditionId;
    this.#conditionValuesSetHashGetter = conditionValuesSetHashGetter;
  }

  setValue (value: unknown[][], customProperties = {}) {
    const conditionValueDto = {
      conditionDefinitionId: this.conditionId,
      conditionDefinitionSetId: this.conditionSetId,
      values: value,
      customProperties
    };
    return ajax<Record<string, string>>(`${this.path}?${PARAMETER_CONDITION_HASH}=${this.conditionValuesSetHash}`, {
      type: 'PUT',
      data: JSON.stringify(conditionValueDto),
      contentType: MIMETYPE_JSON
    });
  }

  setValueIntegration (value: Record<string, string>) {
    const conditionValueIntegrationDto = {
      conditionDefinitionSetId: this.conditionSetId,
      conditionName: this.#conditionName,
      values: value
    };
    return ajax(`${this.path}?${PARAMETER_CONDITION_HASH}=${this.conditionValuesSetHash}`, {
      type: 'PUT',
      data: JSON.stringify(conditionValueIntegrationDto),
      contentType: MIMETYPE_DISY_POST_MESSAGE
    });
  }

  getValue () {
    return ajax(this.path, { dataType: 'json', data: { conditionValuesSetHash: this.conditionValuesSetHash } });
  }

  get conditionSetId () {
    return this.#conditionSetId;
  }

  get conditionId () {
    return this.#conditionId;
  }

  /** @return The condition value set hash for this condition */
  protected get conditionValuesSetHash () {
    return this.#conditionValuesSetHashGetter();
  }

  /** @return The map of condition query params */
  protected get conditionQueryParams () {
    return {
      [PARAMETER_CONDITION_SET_ID]: encodeURIComponent(this.conditionSetId),
      [PARAMETER_CONDITION_DEFINITION_ID]: this.conditionId,
      [PARAMETER_CONDITION_HASH]: this.conditionValuesSetHash
    };
  }

  /** @return The path to make the request to */
  protected get path () {
    const encodedConditionSetId = encodePathVariable(this.conditionSetId);
    const encodedConditionId = encodePathVariable(this.conditionId);
    return `/conditionsets/${encodedConditionSetId}/conditions/${encodedConditionId}/conditionvalues`;
  }

}
